import classNames from 'classnames';
import Checkbox from 'components/atoms/Checkbox';
import Label from 'components/foundations/Label';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import IconMobile from 'resources/svg/icon_mobile.svg';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import css from './PicturizedMultiSelect.css';

const PicturizedMultiSelect = ({
    allItems,
    filteredItems,
    onChange,
    className,
    selectAll,
    translate,
    itemType = '',
}) => {
    const [expandedParentItems, setExpandedParentItems] = useState({});
    const selectedCount = allItems.filter(item => item.selected).length;
    const totalCount = allItems.length;

    const toggleExpandedItem = id => setExpandedParentItems(prev => ({ ...prev, [id]: !prev[id] }));

    const renderItem = ({
        item: { Icon: IconComponent = IconMobile, iconProps = {}, name, selected, id, displayName },
        onClick,
        onToggle = onChange,
        isSubItem = false,
        className: itemClassName = '',
        children,
        showIcon = true,
        indeterminate = false,
        info = '',
    }) => (
        <div key={id} className={classNames(css.item, itemClassName)} onClick={onClick}>
            <Checkbox
                className={css.checkbox}
                checked={selected}
                indeterminate={indeterminate}
                size="small"
                onChange={newState => onToggle(id, newState, isSubItem)}
            />
            {children}
            <div className={css.itemContent}>
                {showIcon && <IconComponent className={css.logo} {...iconProps} />}
                <div className={classNames(css.name, { [css.withInfo]: info })}>{displayName || name}</div>
                {info && <Label type="p" text={info} />}
            </div>
        </div>
    );

    const renderParentItem = item =>
        renderItem({
            item,
            onClick: () => toggleExpandedItem(item.id),
            className: css.parentItem,
            children: (
                <div className={css.toggleSection}>
                    <div className={css.separator} />
                    <Icon
                        name={expandedParentItems[item.id] ? 'chevron-down' : 'chevron-right'}
                        size={IconSizes.X_SMALL}
                    />
                </div>
            ),
            info: translate('STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.ITEM_SELECTED', {
                selected: item.subItems.filter(subItem => subItem.selected).length,
                total: item.subItems.length,
                itemType,
            }),
            indeterminate: !item.selected && item.subItems.some(subItem => subItem.selected),
        });

    const renderSubItem = item =>
        renderItem({
            item,
            onClick: () => onChange(item.id, !item.selected, true),
            className: css.subItem,
            isSubItem: true,
        });

    const renderSelectAllItem = () =>
        renderItem({
            item: {
                name: translate('STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.SELECT_ALL', {
                    count: totalCount,
                }),
                id: 'all',
                selected: selectedCount === totalCount,
            },
            onClick: () => selectAll(selectedCount !== totalCount),
            onToggle: () => selectAll(selectedCount !== totalCount),
            className: css.selectAllItems,
            showIcon: false,
        });

    const getPriority = item => {
        if (item.selected) return 0;
        if (item.subItems?.some(subItem => subItem.selected)) return 1;
        return 2;
    };

    return (
        <div className={className}>
            {renderSelectAllItem()}
            {filteredItems
                ?.sort((a, b) => getPriority(a) - getPriority(b))
                .map(item => {
                    const hasSubItems = item.subItems?.length > 0;
                    const elements = [
                        hasSubItems
                            ? renderParentItem(item)
                            : renderItem({
                                  item,
                                  onClick: () => onChange(item.id, !item.selected),
                              }),
                    ];
                    if (hasSubItems && expandedParentItems[item.id]) {
                        elements.push(
                            <div className={css.subItems}>
                                {item.subItems
                                    .sort((a, b) => getPriority(a) - getPriority(b))
                                    .map(subItem => renderSubItem(subItem))}
                            </div>
                        );
                    }
                    return elements;
                })}
        </div>
    );
};

const itemShape = PropTypes.shape({
    name: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    selected: PropTypes.bool.isRequired,
    Icon: PropTypes.elementType,
    icon: PropTypes.string.isRequired,
});

PicturizedMultiSelect.propTypes = {
    allItems: PropTypes.arrayOf(itemShape).isRequired,
    filteredItems: PropTypes.arrayOf(itemShape).isRequired,
    onChange: PropTypes.func.isRequired,
    selectAll: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
    itemType: PropTypes.string,
    className: PropTypes.string,
};

export default withLocalize(PicturizedMultiSelect);
